import { MapPin } from 'lucide-react'

import type { Parcel } from '../api/types'
import { de } from '../i18n/de'
import { parcelTitle, siteLabel } from '../lib/parcel'
import { carrierSwatch } from '../lib/status'
import { StatusRoute } from './StatusRoute'

/**
 * What wants someone today, above everything else: on the van, waiting at a pickup point,
 * or stuck. Answers the morning question without scrolling. Not shown when nothing is due,
 * so a quiet day costs no space.
 *
 * Tiles in a grid on a wide screen; on a phone one row that scrolls sideways.
 */
export function TodayStrip({
  parcels,
  onOpen,
}: {
  parcels: Parcel[]
  onOpen: (id: number) => void
}) {
  if (parcels.length === 0) return null

  return (
    <section aria-labelledby="today-title">
      <div className="mb-2 flex items-baseline gap-2.5 px-1">
        <h2 id="today-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {de.today.title}
        </h2>
        <span className="rounded-full bg-indigo-500 px-2 py-0.5 text-xs font-semibold text-slate-950 tabular-nums">
          {parcels.length}
        </span>
        <span className="hidden truncate text-xs text-slate-600 sm:inline dark:text-slate-400">
          {de.today.hint}
        </span>
      </div>
      <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
        {parcels.map((parcel) => (
          <li key={parcel.id} className="w-[78%] shrink-0 snap-start sm:w-auto">
            <TodayTile parcel={parcel} onOpen={onOpen} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function TodayTile({ parcel, onOpen }: { parcel: Parcel; onOpen: (id: number) => void }) {
  const title = parcelTitle(parcel)
  const site = siteLabel(parcel)
  // The one line under the title says what to do or where: the pickup point, what went
  // wrong, or where it is being delivered.
  const where =
    parcel.status === 'PICKUP_READY' && parcel.pickup_location
      ? parcel.pickup_location
      : parcel.status === 'EXCEPTION' && parcel.status_detail
        ? parcel.status_detail
        : site

  return (
    <button
      type="button"
      onClick={() => onOpen(parcel.id)}
      className="flex h-full w-full flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700 dark:hover:bg-slate-800/60"
    >
      <span className="self-start">
        <StatusRoute status={parcel.status} />
      </span>
      <span
        className={`line-clamp-2 text-sm ${
          title.fallback
            ? 'text-slate-500 dark:text-slate-400'
            : 'font-medium text-slate-900 dark:text-slate-100'
        }`}
      >
        {title.text}
      </span>
      <span className="mt-auto flex min-w-0 items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <span
          className={`h-2 w-2 shrink-0 rounded-sm ${carrierSwatch(parcel.carrier_code)}`}
          aria-hidden
        />
        <span className="shrink-0">{parcel.carrier_name}</span>
        {where && (
          <>
            {parcel.status === 'PICKUP_READY' && parcel.pickup_location ? (
              <MapPin className="ml-1 h-3 w-3 shrink-0" aria-hidden />
            ) : (
              <span aria-hidden>·</span>
            )}
            <span className="truncate">{where}</span>
          </>
        )}
      </span>
    </button>
  )
}
